import { Box, Chip, Typography, Container } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { dataCenterRegionFromUrl } from "../utils/zoho";
import {
  BORDER_LIGHT,
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  BRAND_COLOR_TINT,
  ERROR_BORDER,
  SUCCESS_COLOR,
  SUCCESS_TEXT,
  SUCCESS_TINT,
  SURFACE,
  SURFACE_TINT,
  TEXT_FAINT,
  TEXT_MUTED,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  WARNING_TEXT,
  WARNING_TINT,
} from "../utils/colors";

const cardSx = {
  borderRadius: "10px",
  border: `1px solid ${BORDER_LIGHT}`,
  bgcolor: SURFACE,
};

const DetailTile = ({ icon, label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 200,
      borderRadius: "8px",
      border: `1px solid ${BORDER_LIGHT}`,
      bgcolor: SURFACE_TINT,
      px: 2,
      py: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
      {icon}
      <Typography sx={{ fontSize: 13, color: TEXT_MUTED }}>
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: 15,
        fontWeight: 600,
        color: TEXT_PRIMARY,
        wordBreak: "break-all",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export default function Overview({
  handleRevokeZoho,
  revokeLoadingZoho,
  authLoadingZoho,
  zohoAuth,
  handleAuthenticateZoho,
  orgId,
  dataCenterUrl,
}) {
  const tileIconSx = { fontSize: 16, color: TEXT_FAINT };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Connection summary */}
      <Box
        sx={{
          ...cardSx,
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: BRAND_COLOR_TINT,
            }}
          >
            <StorageRoundedIcon sx={{ fontSize: 28, color: BRAND_COLOR }} />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                Zoho CRM
              </Typography>
              {zohoAuth ? (
                <Chip
                  size="small"
                  label="Connected"
                  sx={{
                    fontWeight: 500,
                    color: SUCCESS_TEXT,
                    bgcolor: SUCCESS_TINT,
                  }}
                />
              ) : (
                <Chip
                  size="small"
                  label="Not Connected"
                  sx={{
                    fontWeight: 500,
                    color: WARNING_TEXT,
                    bgcolor: WARNING_TINT,
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{ fontSize: 14, color: TEXT_MUTED, mt: 0.25 }}
            >
              {zohoAuth
                ? "Your Zoho CRM account is connected and ready to sync data with your MySQL database."
                : "Authenticate your Zoho CRM account to sync data with your MySQL database."}
            </Typography>
          </Box>
        </Box>

        {zohoAuth ? (
          <Box sx={{ pr: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: "0.6px",
                color: TEXT_FAINT,
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Connection Status
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: SUCCESS_COLOR,
                }}
              />
              <Typography sx={{ fontWeight: 600, color: SUCCESS_COLOR }}>
                Healthy
              </Typography>
            </Box>
          </Box>
        ) : (
          <LoadingButton
            loading={authLoadingZoho}
            loadingIndicator="Waiting for authorization..."
            onClick={handleAuthenticateZoho}
            variant="contained"
            startIcon={<LinkRoundedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              minWidth: 250,
              whiteSpace: "nowrap",
              bgcolor: BRAND_COLOR,
              "&:hover": { bgcolor: BRAND_COLOR_DARK },
              "& .MuiLoadingButton-loadingIndicator": {
                whiteSpace: "nowrap",
              },
            }}
          >
            Authenticate Zoho CRM
          </LoadingButton>
        )}
      </Box>

      {zohoAuth && (
        <>
          {/* Connection details */}
          <Box sx={{ ...cardSx, p: 3, mt: 3 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
              Connection Details
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <DetailTile
                icon={<PublicRoundedIcon sx={tileIconSx} />}
                label="Data Center"
                value={dataCenterRegionFromUrl(dataCenterUrl)}
              />
              <DetailTile
                icon={<BusinessRoundedIcon sx={tileIconSx} />}
                label="Organization ID"
                value={orgId}
              />
              <DetailTile
                icon={<LinkRoundedIcon sx={tileIconSx} />}
                label="API URL"
                value={dataCenterUrl}
              />
            </Box>
          </Box>

          {/* Revoke access — transparent danger-zone card */}
          <Box
            sx={{
              mt: 3,
              borderRadius: "10px",
              border: `1px solid ${ERROR_BORDER}`,
              bgcolor: "transparent",
              px: 3,
              py: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <WarningAmberRoundedIcon
                sx={{ color: "error.main", mt: 0.25 }}
              />
              <Box>
                <Typography
                  sx={{ fontWeight: 600, color: "error.main", fontSize: 15 }}
                >
                  Revoke Access
                </Typography>
                <Typography
                  sx={{ fontSize: 13.5, color: TEXT_SECONDARY }}
                >
                  Revoking access will stop all synchronization between Zoho
                  CRM and your MySQL database. Existing data in MySQL will not
                  be deleted.
                </Typography>
              </Box>
            </Box>

            <LoadingButton
              loading={revokeLoadingZoho}
              onClick={handleRevokeZoho}
              variant="outlined"
              color="error"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                whiteSpace: "nowrap",
                bgcolor: "transparent",
              }}
            >
              Revoke Access
            </LoadingButton>
          </Box>
        </>
      )}
    </Container>
  );
}
